import {REGIONS,LEGACY_ROUTES} from '../../../shared/regions.mjs';
import { LADDER, WALKWAYS, type MotionWorld } from './locomotion';
import sanctuaries from '../../../data/world-sanctuaries.json';
import type { EnemyId } from './enemies';

export type SanctuaryId = keyof typeof sanctuaries;
export type MapId = 'village' | 'grove' | 'temple' | 'cemetery' | SanctuaryId | 'bang-sai-road' | 'moon-town' | 'moon-road' | 'moon-forest' | 'stone-town' | 'stone-road' | 'stone-forest' | 'mist-town' | 'mist-road' | 'mist-forest' | 'dan-sai-town' | 'dan-sai-road' | 'dan-sai-forest' | 'nong-khai-town' | 'nong-khai-road' | 'nong-khai-forest' | 'laplae-town' | 'laplae-road' | 'laplae-forest' | 'wat-moon-harbor' | 'wat-stone-pass' | 'wat-last-light' | 'wat-mask-lantern' | 'wat-mekong-light' | 'wat-woven-name' | 'registry-den';
export type GameMap = MotionWorld & {
  id: MapId;
  name: string;
  tint: number;
  enemy: EnemyId;
  safe?: boolean;
  portals: { x: number; target: MapId; label: string }[];
};

const sanctuaryMaps = {} as Record<SanctuaryId, GameMap>;
for (const [rawId, rawMap] of Object.entries(sanctuaries)) {
  const id = rawId as SanctuaryId;
  sanctuaryMaps[id] = {
    id,
    name: rawMap.name,
    width: rawMap.width,
    tint: id === 'town' ? 0xe2d3a7 : 0xc8d6b3,
    enemy: 'pop',
    safe: true,
    surfaces: [],
    ladder: { x: 500, depth: -36, height: 0 },
    portals: rawMap.portals.map((portal) => ({
      x: portal.x,
      target: portal.target as MapId,
      label: portal.label,
    })),
  };
}

const regionMaps=Object.fromEntries<GameMap>(Object.values(REGIONS).map(area=>[area.id,{id:area.id as MapId,name:area.name,width:area.width,tint:area.tint,enemy:area.view==='combat'?'pop':'tanee',safe:area.safe,surfaces:[],ladder:{x:500,depth:0,height:0},portals:[]}])) as Record<MapId,GameMap>;
export const GAME_MAPS:Record<MapId,GameMap> = {
  ...regionMaps,
  ...sanctuaryMaps,
  'bang-sai-road':{id:'bang-sai-road',name:'ทางเกวียนบางไทร',width:2800,tint:0xc7d6a8,enemy:'pop',safe:true,surfaces:[],ladder:{x:500,depth:0,height:0},portals:[{x:90,target:'town',label:'เมืองศรีสยาม'},{x:2700,target:'grove',label:'ดงตานี'}]},
  cemetery: {
    id: 'cemetery',
    name: 'ป่าช้าริมคลอง',
    width: 3200,
    tint: 0xa7a3bd,
    enemy: 'violent-dead',
    ladder: { x: 620, depth: -36, height: 80 },
    surfaces: [
      { id: 'burial-rise', left: 350, right: 600, depth: -36, height: 0, endHeight: 80, halfDepth: 28 },
      { id: 'burial-ledge', left: 600, right: 1050, depth: -36, height: 80, halfDepth: 24 },
      { id: 'canal-bridge', left: 1380, right: 1820, depth: -36, height: 64, halfDepth: 24 },
      { id: 'spirit-rise', left: 2260, right: 2510, depth: -36, height: 0, endHeight: 112, halfDepth: 28 },
      { id: 'spirit-ledge', left: 2510, right: 2910, depth: -36, height: 112, halfDepth: 24 },
    ],
    portals: [{ x: 90, target: 'temple', label: 'วัดร้าง' }],
  },
  village: {
    id: 'village',
    name: 'บ้านบางผีหลอก',
    width: 4800,
    tint: 0xffffff,
    enemy: 'pop',
    ladder: LADDER,
    surfaces: [
      ...WALKWAYS,
      { id: 'hill', left: 1450, right: 1810, depth: -36, height: 0, endHeight: 96, halfDepth: 28 },
      { id: 'ridge', left: 1810, right: 2200, depth: -36, height: 96, halfDepth: 28 },
      { id: 'bridge', left: 2530, right: 2910, depth: -36, height: 80, halfDepth: 24 },
      { id: 'high-shrine', left: 3340, right: 3790, depth: -36, height: 128, halfDepth: 24 },
    ],
    portals: [
      { x: 90, target: 'temple', label: 'วัดร้าง' },
      { x: 4700, target: 'grove', label: 'ดงตานี' },
      { x: 300, target: 'town', label: 'เมืองศรีสยาม' },
    ],
  },
  grove: {
    id: 'grove',
    name: 'ดงตานีริมน้ำ',
    width: 3600,
    tint: 0x9abfa8,
    enemy: 'tanee',
    ladder: { x: 650, depth: -36, height: 96 },
    surfaces: [
      { id: 'grove-hill', left: 340, right: 600, depth: -36, height: 0, endHeight: 96, halfDepth: 28 },
      { id: 'canopy', left: 600, right: 1120, depth: -36, height: 96, halfDepth: 24 },
      { id: 'river-deck', left: 1350, right: 1720, depth: -36, height: 64, halfDepth: 24 },
      { id: 'grove-rise', left: 2090, right: 2410, depth: -36, height: 0, endHeight: 128, halfDepth: 28 },
      { id: 'grove-top', left: 2410, right: 2940, depth: -36, height: 128, halfDepth: 24 },
    ],
    portals: [
      { x: 90, target: 'village', label: 'บ้านบางผีหลอก' },
      { x: 3500, target: 'temple', label: 'วัดร้าง' },
      {x:3100,target:'bang-sai-road',label:'ทางเกวียนบางไทร'},
    ],
  },
  temple: {
    id: 'temple',
    name: 'ทางสู่วัดร้าง',
    width: 4000,
    tint: 0xb0b0cc,
    enemy: 'krasue',
    ladder: { x: 730, depth: -36, height: 96 },
    surfaces: [
      ...Array.from({ length: 4 }, (_, i) => ({
        id: `temple-step-${i}`,
        left: 400 + i * 70,
        right: i === 3 ? 1050 : 470 + i * 70,
        depth: -36,
        height: (i + 1) * 24,
        halfDepth: 24,
      })),
      { id: 'temple-rise', left: 1560, right: 1930, depth: -36, height: 0, endHeight: 128, halfDepth: 28 },
      { id: 'temple-top', left: 1930, right: 2450, depth: -36, height: 128, halfDepth: 28 },
      { id: 'temple-pier', left: 2780, right: 3260, depth: -36, height: 80, halfDepth: 24 },
    ],
    portals: [
      { x: 90, target: 'grove', label: 'ดงตานี' },
      { x: 3900, target: 'village', label: 'บ้านบางผีหลอก' },
      { x: 2000, target: 'cemetery', label: 'ป่าช้าริมคลอง' },
      { x: 1200, target: 'wat-sila-ngam', label: 'วัดศิลางาม' },
    ],
  },
};
for(const [id,route] of Object.entries(LEGACY_ROUTES)){const map=GAME_MAPS[id as MapId];map.width=route.width;map.portals=route.portals.map(p=>({x:p.x,target:p.target as MapId,label:GAME_MAPS[p.target as MapId].name}));}

for(const area of Object.values(REGIONS)){if(!GAME_MAPS[area.id as MapId])GAME_MAPS[area.id as MapId]={id:area.id as MapId,name:area.name,width:area.width,tint:area.tint,enemy:area.view==='combat'?'pop':'tanee',safe:area.safe,surfaces:[],ladder:{x:500,depth:0,height:0},portals:[]};const map=GAME_MAPS[area.id as MapId];for(const exit of area.exits)if(!map.portals.some(p=>p.target===exit.target))map.portals.push({x:exit.x,target:exit.target as MapId,label:exit.label});}

export function isSanctuary(id: string): id is SanctuaryId {
  return Object.hasOwn(sanctuaries, id)||REGIONS[id]?.safe===true;
}
